import React, { useState } from "react";
import {
  ChevronRight,
  MapPin,
  Clock,
  Users,
  Heart,
  HandshakeIcon,
  BookOpen,
  Music,
  Users as UsersIcon,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import styles from "../styles/FellowshipPage.module.css";
import heroBackground from "../assets/images/image8.png";
import welcomeImage from "../assets/images/img1.png";
import churchImage from "../assets/images/church.png";
import EventNotificationPopup from "../components/EventNotificationPopup"; 

export default function FellowshipPage() {
  const navigate = useNavigate();
  const [expandedLocation, setExpandedLocation] = useState(null);
  const [selectedExpectation, setSelectedExpectation] = useState(0);
  const [activeRegion, setActiveRegion] = useState("Rwanda");

  const regions = {
    Rwanda: [
      {
        name: "Kigali Main Parish",
        address: "Kigali, Rwanda",
        times: ["9am", "11am"],
        special: "Main worship center with full amenities",
        image: churchImage,
      },
      {
        name: "Mbyo Parish",
        address: "Mbyo, Rwanda",
        times: ["9am", "11am"],
        special: "Community-focused worship",
        image: churchImage,
      },
      {
        name: "Mbarara Parish",
        address: "Mbarara, Rwanda",
        times: ["9am", "11am"],
        special: "Youth-oriented services",
        image: churchImage,
      },
      {
        name: "Karangazi Parish",
        address: "Karangazi, Rwanda",
        times: ["9am", "11am"],
        special: "Family-friendly environment",
        image: churchImage,
      },
    ],
    DRC: [
      {
        name: "Bukavu Parish",
        address: "Bukavu, DRC",
        times: ["9am", "11am"],
        special: "Cross-border ministry",
        image: churchImage,
      },
      {
        name: "Goma Parish",
        address: "Goma, DRC",
        times: ["9am", "11am"],
        special: "Community outreach programs",
        image: churchImage,
      },
    ],
    Europe: [
      {
        name: "Norway Parish",
        address: "Oslo, Norway",
        times: ["9am", "11am"],
        special: "Scandinavian ministry hub",
        image: churchImage,
      },
      {
        name: "Belgium Parish",
        address: "Brussels, Belgium",
        times: ["9am", "11am"],
        special: "European worship center",
        image: churchImage,
      },
    ],
    Diaspora: [
      {
        name: "Canada Parish",
        address: "Toronto, Canada",
        times: ["9am", "11am"],
        special: "International worship community",
        image: churchImage,
      },
    ],
  };

  const expectations = [
    {
      icon: <HandshakeIcon size={32} />,
      title: "A Warm Welcome",
      description:
        "When you step into our church, we hope you feel at home. We may all come from different backgrounds or life stages, but Sundays are a safe space to arrive as you are and be welcomed in like family.",
      image:
        "https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&h=600&fit=crop",
    },
    {
      icon: <BookOpen size={32} />,
      title: "A Message From the Bible",
      description:
        "Our worship services revolve around Jesus. Each week, you'll hear someone preach on a passage from the Bible that's meant to change us from the inside out.",
      image:
        "https://images.unsplash.com/photo-1516975080664-ed2fc6a32937?w=800&h=600&fit=crop",
    },
    {
      icon: <Music size={32} />,
      title: "A Time of Worship",
      description:
        "Every day, we worship God through our actions, time, and resources. But on Sundays, we get to worship Him with one another through song.",
      image:
        "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=800&h=600&fit=crop",
    },
    {
      icon: <UsersIcon size={32} />,
      title: "A Place to Connect With Others",
      description:
        "You were made to be known by others. Sunday mornings are more than a weekly message; they're an opportunity to meet and begin to grow in community.",
      image:
        "https://images.unsplash.com/photo-1517457373614-b7152f800fd1?w=800&h=600&fit=crop",
    },
  ];

  const features = [
    {
      icon: <Heart size={48} />,
      title: "Spiritual Growth",
      description:
        "Deepen your faith through inspiring messages, worship, and prayer with our community.",
    },
    {
      icon: <HandshakeIcon size={48} />,
      title: "Community",
      description:
        "Build meaningful relationships with people who share your values and beliefs.",
    },
    {
      icon: <Users size={48} />,
      title: "Service",
      description:
        "Make a difference in the lives of others through various ministry opportunities.",
    },
  ];

  const handleLocationClick = (index) => {
    setExpandedLocation(expandedLocation === index ? null : index);
  };

  const handleNavigation = (path) => {
    navigate(path);
  };

  const handleJoinUs = (e) => {
    e.preventDefault();
    // Does not navigate anywhere
  };

  return (
    <div>
      {/* Hero Section */}
      <section
        className={styles.hero}
        style={{
          backgroundImage: `url(${heroBackground})`,
        }}
      >
        <div className={styles.heroOverlay} />
        <div className={styles.heroContent}>
          <div className={styles.animateFadeInUp}>
            <h1 className={styles.heroTitle}>
              Join Us
              <br />
              This Sunday!
            </h1>

            <p className={styles.heroDescription}>
              Experience authentic worship, hear God's Word, and connect with a
              community that cares about you.
            </p>

            <div className={styles.heroButtonsRow}>
              <button className={`${styles.btnPrimary} ${styles.hoverLift}`}>
                Locations & Times
                <ChevronRight size={20} />
              </button>
              <button
                className={`${styles.btnPrimary} ${styles.hoverLift}`}
                onClick={handleJoinUs}
              >
                Join Our Community
                <ChevronRight size={20} />
              </button>
            </div>
          </div>
        </div>
        
        {/* Add Event Notification Popup Here */}
        <EventNotificationPopup />
      </section>

      {/* Welcome Section */}
      <section className={`${styles.section} ${styles.sectionWhite}`}>
        <div className={styles.contentWrapper}>
          <div className={styles.welcomeLayout}>
            <div className={styles.welcomeImage}>
              <img
                src={welcomeImage}
                alt="Calvary Wide Fellowship Community"
                className={styles.welcomeImg}
              />
            </div>
            <div className={styles.welcomeContent}>
              <div className={styles.sectionLabel}>
                WELCOME TO CALVARY WIDE FELLOWSHIP
              </div>
              <h2 className={styles.welcomeSectionTitle}>
                We're a New Testament church existing for the supremacy of the
                name and purpose of Jesus Christ. We believe Christ's life,
                death, and resurrection are essential to God's plan to heal and
                redeem our fallen world. And we're dedicated to being a part of
                what He's doing in our city and among the unreached.
              </h2>
              <div className={styles.welcomeButtons}>
                <button
                  className={`${styles.btnAccent} ${styles.hoverLift}`}
                  onClick={() => handleNavigation("/about")}
                >
                  Learn More
                  <ChevronRight size={20} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Join Section */}
      <section className={`${styles.section} ${styles.sectionGray}`}>
        <div className={styles.contentWrapper}>
          <div className={styles.sectionDescLeft}>
            <span className={styles.sectionLabel}>Benefits</span>
            <h2 className={`${styles.sectionTitle} ${styles.sectionTitleLeft}`}>
              Why Join Us?
            </h2>
          </div>

          <div className={styles.featuresGrid}>
            {features.map((feature, index) => (
              <div key={index} className={styles.featureCard}>
                <div className={styles.featureIconContainer}>
                  {feature.icon}
                </div>
                <h3 className={styles.featureTitle}>{feature.title}</h3>
                <p className={styles.featureText}>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Locations Section with Tabs */}
      <section className={`${styles.section} ${styles.sectionWhite}`}>
        <div className={styles.contentWrapper}>
          <div className={styles.sectionDescLeft}>
            <span className={styles.sectionLabel}>Multiple Locations</span>
            <h2 className={`${styles.sectionTitle} ${styles.sectionTitleLeft}`}>
              One Church, Many Communities
            </h2>
            <p className={styles.sectionDesc}>
              Calvary Wide Fellowship is one church with multiple congregations
              across four regions.
            </p>
          </div>

          {/* Region Tabs */}
          <div className={styles.regionTabs}>
            {Object.keys(regions).map((region) => (
              <button
                key={region}
                className={`${styles.regionTab} ${
                  activeRegion === region ? styles.regionTabActive : ""
                }`}
                onClick={() => setActiveRegion(region)}
              >
                {region}
                <span className={styles.parishCount}>
                  ({regions[region].length})
                </span>
              </button>
            ))}
          </div>

          {/* Locations Grid */}
          <div className={styles.locationsGrid}>
            {regions[activeRegion].map((location, index) => (
              <div
                key={index}
                className={styles.locationCard}
                onClick={() => handleLocationClick(index)}
              >
                <img
                  src={location.image}
                  alt={location.name}
                  className={styles.locationImage}
                />
                <div className={styles.locationCardContent}>
                  <h3 className={styles.locationName}>{location.name}</h3>
                  {location.special && (
                    <span className={styles.specialNote}>
                      {location.special}
                    </span>
                  )}

                  <div className={styles.infoItem}>
                    <MapPin size={18} />
                    <span className={styles.infoItemText}>
                      {location.address}
                    </span>
                  </div>

                  <div className={styles.infoItem}>
                    <Clock size={18} />
                    <div className={styles.serviceTimes}>
                      <span className={styles.serviceTimesLabel}>
                        Service Times
                      </span>
                      <div className={styles.times}>
                        {location.times.map((time, i) => (
                          <span key={i} className={styles.timeSlot}>
                            {time}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  <button
                    className={`${styles.btnBorder} ${styles.hoverLift}`}
                    style={{ width: "100%", marginTop: "24px" }}
                  >
                    Learn More
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What to Expect Section */}
      <section className={`${styles.section} ${styles.sectionGray}`}>
        <div className={styles.contentWrapper}>
          <div className={styles.sectionDescLeft}>
            <span className={styles.sectionLabel}>Sunday Experience</span>
            <h2 className={`${styles.sectionTitle} ${styles.sectionTitleLeft}`}>
              What to Expect
            </h2>
            <p className={styles.sectionDesc}>You belong here</p>
          </div>

          <div className={styles.expectationsGrid}>
            {expectations.map((item, index) => (
              <div
                key={index}
                className={`${styles.expectationCard} ${
                  selectedExpectation === index
                    ? styles.expectationCardActive
                    : ""
                }`}
                onMouseEnter={() => setSelectedExpectation(index)}
                onClick={() => setSelectedExpectation(index)}
              >
                <div className={styles.expectationCardContent}>
                  <div className={styles.expectationHeader}>
                    <div className={styles.expectationIcon}>{item.icon}</div>
                    <h3 className={styles.expectationTitle}>{item.title}</h3>
                  </div>
                  <p className={styles.expectationText}>{item.description}</p>
                </div>
                <div className={styles.expectationImage}>
                  <img
                    src={item.image}
                    alt={item.title}
                    className={styles.cardImage}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className={styles.sectionBlue}>
        <div
          className={`${styles.contentWrapper} ${styles.section}`}
          style={{ position: "relative", zIndex: 10 }}
        >
          <div style={{ textAlign: "center" }}>
            <h2 className={styles.sectionTitle}>
              Ready to Join Our Fellowship?
            </h2>
            <p className={styles.sectionDesc}>
              Take the first step towards a meaningful spiritual journey with
              our community
            </p>

            <div className={styles.heroButtons}>
              <button className={`${styles.btnPrimary} ${styles.hoverLift}`}>
                Visit This Sunday
                <ChevronRight size={20} />
              </button>
              <button
                className={`${styles.btnSecondary} ${styles.hoverLift}`}
                onClick={() => navigate("/membership")}
              >
                Learn More
                <ChevronRight size={20} />
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}